import { compactPromotionPaymentFields } from "../supabase/edge-functions/_shared/promotionPayments";

const STORAGE_PREFIX = "mozzoapp:inicio-orders:";

export const OPERATIONAL_ORDERS_UPDATED_EVENT =
  "mozzoapp:operational-orders-updated";

export const buildOperationalOrdersStorageKey = (restaurantId) =>
  `${STORAGE_PREFIX}${restaurantId || "default"}`;

const normalizeKitchenModeSnapshot = (value) =>
  value === "screen" || value === "printer" ? value : "manual";

const SALON_TABLE_SHAPES = new Set(["circle", "square", "rectangle"]);
const DEFAULT_SALON_TABLE_GEOMETRY = Object.freeze({
  shape: "circle",
  width: 88,
  height: 88,
  rotation: 0,
});
const clampSalonTableDimension = (value, axis = "width") => {
  const numericValue = Number(value);
  const fallback =
    axis === "height"
      ? DEFAULT_SALON_TABLE_GEOMETRY.height
      : DEFAULT_SALON_TABLE_GEOMETRY.width;
  const minimum = axis === "height" ? 52 : 64;
  const maximum = axis === "height" ? 140 : 180;

  return Math.min(
    maximum,
    Math.max(minimum, Number.isFinite(numericValue) ? numericValue : fallback)
  );
};
const normalizeSalonTableRotation = (value) => {
  const normalized = Math.round(Number(value || 0));
  return normalized === 90 || normalized === 270 ? 90 : 0;
};
const normalizeSalonTableGeometry = (value = {}) => {
  const requestedShape = String(value?.shape || "").trim().toLowerCase();
  const shape = SALON_TABLE_SHAPES.has(requestedShape)
    ? requestedShape
    : DEFAULT_SALON_TABLE_GEOMETRY.shape;
  const width = clampSalonTableDimension(value?.width, "width");

  if (shape !== "rectangle") {
    return { shape, width, height: width, rotation: 0 };
  }

  return {
    shape,
    width: Math.max(80, width),
    height: clampSalonTableDimension(value?.height || 76, "height"),
    rotation: normalizeSalonTableRotation(value?.rotation),
  };
};

const normalizeSalonLayout = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((result, [tableId, position]) => {
    const safeTableId = String(tableId || "").trim().slice(0, 120);
    const x = Number(position?.x);
    const y = Number(position?.y);

    if (!safeTableId || !Number.isFinite(x) || !Number.isFinite(y)) {
      return result;
    }

    result[safeTableId] = {
      x: Math.min(94, Math.max(6, x)),
      y: Math.min(90, Math.max(10, y)),
      ...normalizeSalonTableGeometry(position),
    };
    return result;
  }, {});
};

export const normalizeSalonTables = (value) => {
  const seenTableIds = new Set();

  return (Array.isArray(value) ? value : []).reduce((result, table, index) => {
    const id = String(table?.id || "").trim().slice(0, 120);

    if (!id || seenTableIds.has(id)) {
      return result;
    }

    const tableNumber = Math.max(0, Math.round(Number(table?.tableNumber || 0)));
    const label = String(
      table?.label || (tableNumber ? `Mesa ${tableNumber}` : `Mesa ${index + 1}`)
    )
      .trim()
      .slice(0, 100);

    if (!label) {
      return result;
    }

    seenTableIds.add(id);
    result.push({
      id,
      branchId: String(table?.branchId || table?.branch_id || "").trim().slice(0, 120),
      tableNumber,
      label,
      active: table?.active !== false,
    });
    return result;
  }, []);
};

export const normalizeSalonReservations = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((result, [tableId, reservationValue]) => {
    const safeTableId = String(tableId || "").trim().slice(0, 120);

    if (!safeTableId) {
      return result;
    }

    // Las versiones anteriores guardaban una sola reserva como objeto. La
    // convertimos en lista para conservarla y permitir una agenda por mesa.
    const candidates = Array.isArray(reservationValue)
      ? reservationValue
      : reservationValue && typeof reservationValue === "object"
        ? [reservationValue]
        : [];
    const seenIds = new Set();
    const reservations = candidates.reduce((items, reservation, index) => {
      const customerName = String(reservation?.customerName || "").trim().slice(0, 100);
      const reservedFor = String(reservation?.reservedFor || "").trim().slice(0, 40);
      const id = String(
        reservation?.id || `reservation-${safeTableId}-${index}`
      ).slice(0, 160);

      if (!customerName || !reservedFor || !id || seenIds.has(id)) {
        return items;
      }

      seenIds.add(id);
      items.push({
        id,
        tableId: String(reservation?.tableId || safeTableId).trim().slice(0, 120),
        tableLabel: String(reservation?.tableLabel || "").trim().slice(0, 100),
        customerName,
        partySize: Math.min(30, Math.max(1, Number(reservation?.partySize || 1))),
        reservedFor,
        blockMinutesBefore: Math.min(
          360,
          Math.max(0, Number(reservation?.blockMinutesBefore ?? 120))
        ),
        durationMinutes: Math.min(
          480,
          Math.max(30, Number(reservation?.durationMinutes ?? 120))
        ),
        notes: String(reservation?.notes || "").trim().slice(0, 180),
        createdAt: String(reservation?.createdAt || "").trim().slice(0, 40),
        updatedAt: String(reservation?.updatedAt || "").trim().slice(0, 40),
      });
      return items;
    }, []);

    reservations.sort((first, second) => {
      const firstTime = new Date(first.reservedFor).getTime();
      const secondTime = new Date(second.reservedFor).getTime();
      return (Number.isFinite(firstTime) ? firstTime : Number.MAX_SAFE_INTEGER) -
        (Number.isFinite(secondTime) ? secondTime : Number.MAX_SAFE_INTEGER);
    });

    if (reservations.length) {
      result[safeTableId] = reservations.slice(0, 120);
    }

    return result;
  }, {});
};

export const getSalonReservationTimestamp = (reservation) => {
  const timestamp = new Date(reservation?.reservedFor || "").getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const getSalonReservationWindow = (reservation) => {
  const reservedAt = getSalonReservationTimestamp(reservation);
  const blockMinutesBefore = Math.max(
    0,
    Number(reservation?.blockMinutesBefore ?? 120)
  );
  const durationMinutes = Math.max(30, Number(reservation?.durationMinutes ?? 120));

  return {
    reservedAt,
    startsAt: reservedAt - blockMinutesBefore * 60 * 1000,
    endsAt: reservedAt + durationMinutes * 60 * 1000,
  };
};

export const isSalonReservationBlockingTable = (reservation, now = Date.now()) => {
  const { reservedAt, startsAt, endsAt } = getSalonReservationWindow(reservation);
  return Boolean(reservedAt && now >= startsAt && now < endsAt);
};

export const getCurrentSalonReservations = (reservations, now = Date.now()) =>
  (Array.isArray(reservations) ? reservations : [])
    .filter((reservation) => {
      const { reservedAt, endsAt } = getSalonReservationWindow(reservation);
      return Boolean(reservedAt && endsAt > now);
    })
    .sort(
      (first, second) =>
        getSalonReservationTimestamp(first) - getSalonReservationTimestamp(second)
    );

const normalizeSalonReservationTableKey = (value) =>
  String(value || "").trim().toLocaleLowerCase("es-AR");

export const getSalonReservationsForTable = (
  reservationsByTable,
  table,
  now = Date.now()
) => {
  if (!table) {
    return [];
  }

  const tableKeys = new Set(
    [
      table.id,
      table.label,
      table.tableNumber ? `Mesa ${table.tableNumber}` : "",
    ]
      .map(normalizeSalonReservationTableKey)
      .filter(Boolean)
  );
  const uniqueReservations = new Map();

  Object.entries(normalizeSalonReservations(reservationsByTable)).forEach(
    ([storedTableId, reservations]) => {
      (Array.isArray(reservations) ? reservations : []).forEach((reservation) => {
        const reservationKeys = [
          storedTableId,
          reservation?.tableId,
          reservation?.tableLabel,
        ]
          .map(normalizeSalonReservationTableKey)
          .filter(Boolean);

        if (!reservationKeys.some((key) => tableKeys.has(key))) {
          return;
        }

        uniqueReservations.set(reservation.id, reservation);
      });
    }
  );

  return getCurrentSalonReservations(Array.from(uniqueReservations.values()), now);
};

export const getBlockingSalonReservationForTable = (
  reservationsByTable,
  table,
  now = Date.now()
) =>
  getSalonReservationsForTable(reservationsByTable, table, now).find((reservation) =>
    isSalonReservationBlockingTable(reservation, now)
  ) || null;

const DEFAULT_SALON_ROOM_ID = "salon-principal";

const normalizeSalonRooms = (value) => {
  const seenIds = new Set();
  const rooms = (Array.isArray(value) ? value : []).reduce((result, room) => {
    const id = String(room?.id || "").trim().slice(0, 120);
    const name = String(room?.name || "").trim().slice(0, 80);

    if (!id || !name || seenIds.has(id)) {
      return result;
    }

    seenIds.add(id);
    result.push({
      id,
      name,
      createdAt: String(room?.createdAt || "").trim().slice(0, 40),
    });
    return result;
  }, []);

  return rooms.length
    ? rooms.slice(0, 20)
    : [{ id: DEFAULT_SALON_ROOM_ID, name: "Salon principal", createdAt: "" }];
};

const normalizeSalonTableRooms = (value, rooms) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const roomIds = new Set((Array.isArray(rooms) ? rooms : []).map((room) => room.id));

  return Object.entries(value).reduce((result, [tableId, roomId]) => {
    const safeTableId = String(tableId || "").trim().slice(0, 120);
    const safeRoomId = String(roomId || "").trim().slice(0, 120);

    if (safeTableId && roomIds.has(safeRoomId)) {
      result[safeTableId] = safeRoomId;
    }

    return result;
  }, {});
};

const SALON_FIXTURE_TYPES = new Set([
  "bar",
  "counter",
  "entrance",
  "exit",
  "kitchen",
]);

const normalizeSalonFixtures = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((result, [roomId, fixtures]) => {
    const safeRoomId = String(roomId || "").trim().slice(0, 120);

    if (!safeRoomId) {
      return result;
    }

    const seenIds = new Set();
    result[safeRoomId] = (Array.isArray(fixtures) ? fixtures : []).reduce(
      (fixtureResult, fixture) => {
        const id = String(fixture?.id || "").trim().slice(0, 160);
        const type = String(fixture?.type || "").trim().toLowerCase();

        if (!id || !SALON_FIXTURE_TYPES.has(type) || seenIds.has(id)) {
          return fixtureResult;
        }

        seenIds.add(id);
        fixtureResult.push({
          id,
          type,
          label: String(fixture?.label || "").trim().slice(0, 40),
          x: Math.min(99, Math.max(1, Number(fixture?.x || 1))),
          y: Math.min(99, Math.max(1, Number(fixture?.y || 1))),
          width: Math.min(65, Math.max(8, Number(fixture?.width || 8))),
          height: Math.min(40, Math.max(5, Number(fixture?.height || 5))),
        });
        return fixtureResult;
      },
      []
    );

    return result;
  }, {});
};

const normalizeOperationalOrdersState = (payload, fallbackFactory) => {
  const fallbackValue =
    typeof fallbackFactory === "function"
      ? fallbackFactory()
      : {
          mesasActivas: [],
          pedidosHistoricos: [],
          salonTables: [],
          salonLayout: {},
          salonReservations: {},
          salonRooms: [],
          salonTableRooms: {},
          salonFixtures: {},
        };

  const salonRooms = normalizeSalonRooms(payload?.salonRooms || fallbackValue.salonRooms);

  return {
    mesasActivas: Array.isArray(payload?.mesasActivas)
      ? payload.mesasActivas
      : fallbackValue.mesasActivas,
    pedidosHistoricos: (
      Array.isArray(payload?.pedidosHistoricos)
        ? payload.pedidosHistoricos
        : fallbackValue.pedidosHistoricos || []
    ).slice(0, 160),
    salonTables: normalizeSalonTables(payload?.salonTables || fallbackValue.salonTables),
    salonLayout: normalizeSalonLayout(payload?.salonLayout || fallbackValue.salonLayout),
    salonReservations: normalizeSalonReservations(
      payload?.salonReservations || fallbackValue.salonReservations
    ),
    salonRooms,
    salonTableRooms: normalizeSalonTableRooms(
      payload?.salonTableRooms || fallbackValue.salonTableRooms,
      salonRooms
    ),
    salonFixtures: normalizeSalonFixtures(
      payload?.salonFixtures || fallbackValue.salonFixtures
    ),
  };
};

const compactOperationalOrderItem = (item) => {
  if (!item || typeof item !== "object") {
    return item;
  }

  return {
    ...item,
    imageUrl: "",
    signature: "",
  };
};

const compactOperationalPricing = (pricing) => {
  if (!pricing || typeof pricing !== "object") {
    return null;
  }

  return {
    ...compactPromotionPaymentFields(pricing),
    listTotal: Number(pricing.listTotal || 0),
    comboDiscountTotal: Number(pricing.comboDiscountTotal || 0),
    promotionDiscountTotal: Number(pricing.promotionDiscountTotal || 0),
    totalDiscount: Number(pricing.totalDiscount || 0),
    finalTotal: Number(pricing.finalTotal || 0),
    adjustments: Array.isArray(pricing.adjustments)
      ? pricing.adjustments.slice(0, 8).map((adjustment) => ({
          id: adjustment?.id || "",
          type: adjustment?.type || "",
          title: String(adjustment?.title || "").trim().slice(0, 120),
          amount: Number(adjustment?.amount || 0),
          summary: String(adjustment?.summary || "").trim().slice(0, 140),
        }))
      : [],
  };
};

const compactOperationalOrder = (order) => {
  if (!order || typeof order !== "object") {
    return order;
  }

  return {
    ...order,
    mesa: String(order.mesa || "").trim().slice(0, 120),
    canal: String(order.canal || "").trim().slice(0, 60),
    source: String(order.source || "").trim().slice(0, 40),
    kitchenModeSnapshot: normalizeKitchenModeSnapshot(order.kitchenModeSnapshot),
    notes: String(order.notes || "").trim().slice(0, 260),
    pricing: compactOperationalPricing(order.pricing),
    items: Array.isArray(order.items)
      ? order.items.map((item) => compactOperationalOrderItem(item))
      : [],
  };
};

const compactOperationalOrdersState = (payload) => ({
  mesasActivas: Array.isArray(payload?.mesasActivas)
    ? payload.mesasActivas.map((order) => compactOperationalOrder(order))
    : [],
  pedidosHistoricos: Array.isArray(payload?.pedidosHistoricos)
    ? payload.pedidosHistoricos
        .slice(0, 160)
        .map((order) => compactOperationalOrder(order))
    : [],
  salonTables: normalizeSalonTables(payload?.salonTables),
  salonLayout: normalizeSalonLayout(payload?.salonLayout),
  salonReservations: normalizeSalonReservations(payload?.salonReservations),
  salonRooms: normalizeSalonRooms(payload?.salonRooms),
  salonTableRooms: normalizeSalonTableRooms(
    payload?.salonTableRooms,
    normalizeSalonRooms(payload?.salonRooms)
  ),
  salonFixtures: normalizeSalonFixtures(payload?.salonFixtures),
});

const buildEmergencyOperationalOrderItem = (item) => ({
  id: item?.id || "",
  productId: item?.productId || item?.sourceProductId || "",
  productName: String(item?.productName || item?.name || item?.nombre || "Producto")
    .trim()
    .slice(0, 120),
  quantity: Math.max(1, Number(item?.quantity || 1)),
  baseUnitPrice: Number(item?.baseUnitPrice || item?.basePrice || 0),
  extraUnitCost: Number(item?.extraUnitCost || item?.extraCost || 0),
  unitPrice: Number(item?.unitPrice || 0),
  delivered: item?.delivered === true,
  deliveredAt: item?.deliveredAt || "",
  deliveredBy: item?.deliveredBy || "",
  deliveryStatus: String(item?.deliveryStatus || "pending").trim().slice(0, 40),
  deliveryAssignedAt: item?.deliveryAssignedAt || "",
  assignedWaiterUserId: item?.assignedWaiterUserId || "",
  assignedWaiterName: String(item?.assignedWaiterName || "").trim().slice(0, 80),
  sendToKitchen: item?.sendToKitchen !== false,
  consumos: Array.isArray(item?.consumos)
    ? item.consumos.slice(0, 24).map((consumo) => ({
        id: consumo?.id || consumo?.ingredientId || "",
        ingredientId: consumo?.ingredientId || consumo?.id || "",
        ingredientName: String(consumo?.ingredientName || "").trim().slice(0, 80),
        quantity: Number(consumo?.quantity || 0),
        unit: String(consumo?.unit || consumo?.unidad || "un").trim().slice(0, 12),
      }))
    : [],
  selectionDetails: Array.isArray(item?.selectionDetails)
    ? item.selectionDetails
        .filter(Boolean)
        .slice(0, 8)
        .map((detail) => String(detail).trim().slice(0, 100))
    : [],
  nombre: String(item?.nombre || item?.name || item?.productName || "Producto")
    .trim()
    .slice(0, 180),
});

const buildEmergencyOperationalOrder = (order) => ({
  id: order?.id || "",
  mesa: String(order?.mesa || "").trim().slice(0, 120),
  tableId: order?.tableId || "",
  qrToken: order?.qrToken || "",
  customerName: String(order?.customerName || "").trim().slice(0, 80),
  dinerName: String(order?.dinerName || "").trim().slice(0, 80),
  customerSessionId: order?.customerSessionId || "",
  preparationNumber:
    order?.preparationNumber == null ? null : Number(order.preparationNumber || 0),
  comensales: Math.max(1, Number(order?.comensales || 1)),
  canal: String(order?.canal || "").trim().slice(0, 60),
  source: String(order?.source || "").trim().slice(0, 40),
  requestedPaymentMethod: String(order?.requestedPaymentMethod || "").trim().slice(0, 40),
  selectedPaymentMethod: String(order?.selectedPaymentMethod || "").trim().slice(0, 40),
  paymentStatus: String(order?.paymentStatus || "").trim().slice(0, 40),
  cashPaymentPending: order?.cashPaymentPending === true,
  kitchenModeSnapshot: normalizeKitchenModeSnapshot(order?.kitchenModeSnapshot),
  kitchenDispatchStatus: String(order?.kitchenDispatchStatus || "").trim().slice(0, 60),
  kitchenDispatchedAt: order?.kitchenDispatchedAt || "",
  kitchenDispatchedBy: order?.kitchenDispatchedBy || "",
  kitchenPrintedAt: order?.kitchenPrintedAt || "",
  kitchenPrintedBy: order?.kitchenPrintedBy || "",
  kitchenPrintCount: Math.max(0, Number(order?.kitchenPrintCount || 0)),
  phase: String(order?.phase || "new").trim().slice(0, 40),
  stockDiscounted: order?.stockDiscounted === true,
  orderedAt: order?.orderedAt || "",
  notes: String(order?.notes || "").trim().slice(0, 180),
  totalAmount: Number(order?.totalAmount || 0),
  paymentOperationId: order?.paymentOperationId || "",
  pricing: compactOperationalPricing(order?.pricing),
  items: Array.isArray(order?.items)
    ? order.items.slice(0, 40).map((item) => buildEmergencyOperationalOrderItem(item))
    : [],
});

const buildEmergencyOperationalOrdersState = (payload) => ({
  mesasActivas: Array.isArray(payload?.mesasActivas)
    ? payload.mesasActivas.slice(0, 40).map((order) => buildEmergencyOperationalOrder(order))
    : [],
  pedidosHistoricos: Array.isArray(payload?.pedidosHistoricos)
    ? payload.pedidosHistoricos.slice(0, 80).map((order) => buildEmergencyOperationalOrder(order))
    : [],
  salonTables: normalizeSalonTables(payload?.salonTables),
  salonLayout: normalizeSalonLayout(payload?.salonLayout),
  salonReservations: normalizeSalonReservations(payload?.salonReservations),
  salonRooms: normalizeSalonRooms(payload?.salonRooms),
  salonTableRooms: normalizeSalonTableRooms(
    payload?.salonTableRooms,
    normalizeSalonRooms(payload?.salonRooms)
  ),
  salonFixtures: normalizeSalonFixtures(payload?.salonFixtures),
});

const getPendingOrderNotificationIds = (payload) =>
  (Array.isArray(payload?.mesasActivas) ? payload.mesasActivas : [])
    .filter(
      (mesa) =>
        mesa?.phase === "new" &&
        Array.isArray(mesa.items) &&
        mesa.items.every((item) => !item.delivered)
    )
    .map((mesa) => mesa.id)
    .filter(Boolean);

const emitOperationalOrdersUpdate = (restaurant, payload, newNotificationIds = []) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(OPERATIONAL_ORDERS_UPDATED_EVENT, {
      detail: {
        restaurantId: restaurant?.id || "default",
        payload,
        newNotificationIds,
      },
    })
  );
};

export const getOperationalOrdersState = (restaurant, fallbackFactory) => {
  if (typeof window === "undefined") {
    return normalizeOperationalOrdersState(null, fallbackFactory);
  }

  try {
    const rawValue = window.localStorage.getItem(
      buildOperationalOrdersStorageKey(restaurant?.id)
    );

    if (!rawValue) {
      return normalizeOperationalOrdersState(null, fallbackFactory);
    }

    return normalizeOperationalOrdersState(JSON.parse(rawValue), fallbackFactory);
  } catch (error) {
    console.error("No pudimos leer los pedidos operativos:", error);
    return normalizeOperationalOrdersState(null, fallbackFactory);
  }
};

export const saveOperationalOrdersState = (restaurant, payload, fallbackFactory) => {
  const previousState = getOperationalOrdersState(restaurant, fallbackFactory);
  const normalizedPayload = compactOperationalOrdersState(
    normalizeOperationalOrdersState(payload, fallbackFactory)
  );
  const serializedPreviousState = JSON.stringify(previousState);
  const serializedNextState = JSON.stringify(normalizedPayload);
  const previousNotificationIds = new Set(
    getPendingOrderNotificationIds(previousState)
  );
  const nextNotificationIds = getPendingOrderNotificationIds(normalizedPayload);
  const newNotificationIds = nextNotificationIds.filter(
    (id) => !previousNotificationIds.has(id)
  );

  if (typeof window === "undefined") {
    return normalizedPayload;
  }

  if (serializedPreviousState === serializedNextState) {
    return normalizedPayload;
  }

  try {
    window.localStorage.setItem(
      buildOperationalOrdersStorageKey(restaurant?.id),
      serializedNextState
    );
    emitOperationalOrdersUpdate(restaurant, normalizedPayload, newNotificationIds);
  } catch (error) {
    try {
      const emergencyPayload = buildEmergencyOperationalOrdersState(normalizedPayload);
      window.localStorage.setItem(
        buildOperationalOrdersStorageKey(restaurant?.id),
        JSON.stringify(emergencyPayload)
      );
      emitOperationalOrdersUpdate(restaurant, emergencyPayload, newNotificationIds);
      return emergencyPayload;
    } catch (emergencyError) {
      console.error("No pudimos guardar los pedidos operativos:", emergencyError);
      throw new Error("No se pudieron guardar los pedidos del turno.");
    }
  }

  return normalizedPayload;
};

export const appendMesaActivaOrder = (restaurant, order, fallbackFactory) => {
  const currentState = getOperationalOrdersState(restaurant, fallbackFactory);

  return saveOperationalOrdersState(
    restaurant,
    {
      ...currentState,
      mesasActivas: [order, ...(currentState.mesasActivas || [])],
    },
    fallbackFactory
  );
};
